import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
// @ts-ignore
import { ContactService } from '../../services/contact.service';
// @ts-ignore
import { ToastService } from '../../shared/toast/toast.service';
import { SecurityService } from '../../services/security.service';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.css'
})
export class ContactComponent implements OnInit {
  private contactService = inject(ContactService);
  private toastService = inject(ToastService);
  private securityService = inject(SecurityService);
  private route = inject(ActivatedRoute);
  
  name = signal('');
  email = signal('');
  country_code = signal('+961');
  phone = signal('');
  subject = signal('');
  message = signal('');
  loading = signal(false);
  submitted = signal(false);
  errors = signal<{ [key: string]: string }>({});
  
  countryCodes = [
    { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+30', country: 'Greece', flag: '🇬🇷' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  ];

  ngOnInit() {
    // Get subject from query params if provided
    this.route.queryParams.subscribe(params => {
      if (params['subject']) {
        this.subject.set(params['subject']);
      }
    });
  }

  async submitForm() {
    // Check rate limiting - max 5 submissions per minute
    if (!this.securityService.checkRateLimit('contact_form', 5)) {
      this.toastService.error('Too many submissions. Please wait a moment before submitting again.');
      return;
    }

    // Clear previous errors
    this.errors.set({});
    const newErrors: { [key: string]: string } = {};

    // Validate form
    if (!this.name()) newErrors['name'] = 'Full name is required';
    if (!this.email()) newErrors['email'] = 'Email address is required';
    if (!this.country_code()) newErrors['country_code'] = 'Country code is required';
    if (!this.phone()) newErrors['phone'] = 'Phone number is required';
    if (!this.subject()) newErrors['subject'] = 'Subject is required';
    if (!this.message()) newErrors['message'] = 'Message is required';

    // Validate lengths
    if (this.name() && !this.securityService.isValidLength(this.name(), 255)) {
      newErrors['name'] = 'Name is too long (max 255 characters)';
    }
    if (this.email() && !this.securityService.isValidLength(this.email(), 255)) {
      newErrors['email'] = 'Email is too long (max 255 characters)';
    }
    if (this.subject() && !this.securityService.isValidLength(this.subject(), 200)) {
      newErrors['subject'] = 'Subject is too long (max 200 characters)';
    }
    if (this.message() && !this.securityService.isValidLength(this.message(), 5000)) {
      newErrors['message'] = 'Message is too long (max 5000 characters)';
    }

    // Validate email format
    if (this.email()) {
      if (!this.securityService.isValidEmail(this.email())) {
        newErrors['email'] = 'Please enter a valid email address';
      }
    }

    // Validate phone format
    if (this.phone()) {
      if (!this.securityService.isValidPhone(this.phone())) {
        newErrors['phone'] = 'Please enter a valid phone number (digits, spaces, dashes, parentheses)';
      }
    }

    // Show errors if any
    if (Object.keys(newErrors).length > 0) {
      this.errors.set(newErrors);
      this.toastService.error('Please fix the errors below');
      return;
    }

    this.loading.set(true);

    try {
      // Sanitize inputs before sending to database
      const sanitizedData = {
        name: this.securityService.sanitizeFormInput(this.name(), 255),
        email: this.securityService.sanitizeFormInput(this.email(), 255),
        country_code: this.securityService.sanitizeFormInput(this.country_code(), 20),
        phone: this.securityService.sanitizeFormInput(this.phone(), 50),
        subject: this.securityService.sanitizeFormInput(this.subject(), 200),
        message: this.securityService.sanitizeFormInput(this.message(), 5000)
      };

      const result = await (this.contactService as any).submitContactForm(sanitizedData);

      this.toastService.success('Your message has been sent successfully! We will get back to you soon.');
      this.resetForm();
      this.submitted.set(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        this.submitted.set(false);
      }, 5000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      this.toastService.error(message);
    } finally {
      this.loading.set(false);
    }
  }

  private resetForm() {
    this.name.set('');
    this.email.set('');
    this.country_code.set('+1');
    this.phone.set('');
    this.subject.set('');
    this.message.set('');
    this.errors.set({});
  }
}
